import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconAlertCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import type { Sporocilo } from './asistent';

type Polje = Shema['AsistentPolje'];
type Vrednost = string | number | boolean | null;

/**
 * Parametri kartice asistenta kot obrazec (brez klica umetne inteligence, zato brezplačno): glavni zgoraj (obdobje,
 * združevanje, razvrščanje …), filtri pod njimi. Polja, vrste in nazive pošlje strežnik (iz sheme orodja); napake
 * so ob poljih. »Ponastavi« vrne parametre, ki jih je izbral asistent.
 */
export function ParametriKartice({
  sporociloId,
  polja,
  spremenjeno,
  zapri,
  naSpremembo,
}: {
  sporociloId: number;
  polja: Polje[];
  spremenjeno: boolean;
  zapri: () => void;
  naSpremembo: (s: Sporocilo) => void;
}) {
  const [vrednosti, setVrednosti] = useState<Record<string, Vrednost>>(() =>
    Object.fromEntries(polja.map((p) => [p.ime ?? '', (p.vrednost as Vrednost | undefined) ?? null])),
  );
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<'shrani' | 'ponastavi' | null>(null);

  const poslji = async (kaj: 'shrani' | 'ponastavi') => {
    setDela(kaj);
    setNapake({});
    setNapaka(null);
    try {
      const parametri =
        kaj === 'ponastavi'
          ? undefined
          : Object.fromEntries(Object.entries(vrednosti).filter(([, v]) => v !== null && v !== ''));
      const s = await podatki(
        api.POST('/api/asistent/sporocila/{id}/parametri', {
          params: { path: { id: sporociloId } },
          body: { parametri },
        }),
      );
      naSpremembo(s);
      zapri();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const nastavi = (ime: string, v: Vrednost) => setVrednosti((prej) => ({ ...prej, [ime]: v }));
  const glavna = polja.filter((p) => p.skupina === 'GLAVNO');
  const filtri = polja.filter((p) => p.skupina !== 'GLAVNO');

  return (
    <Modal opened onClose={zapri} title="Prilagodi prikaz" radius="lg" size="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Spremembe so brezplačne – umetna inteligenca se ne kliče, številke se preračunajo iz vaših podatkov.
        </Text>
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          {glavna.map((p) => (
            <PoljeParametra
              key={p.ime}
              p={p}
              vrednost={vrednosti[p.ime ?? '']}
              napaka={napake[p.ime ?? '']}
              nastavi={nastavi}
            />
          ))}
        </SimpleGrid>
        {filtri.length > 0 && (
          <>
            <Divider label="Filtri" labelPosition="left" />
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
              {filtri.map((p) => (
                <PoljeParametra
                  key={p.ime}
                  p={p}
                  vrednost={vrednosti[p.ime ?? '']}
                  napaka={napake[p.ime ?? '']}
                  nastavi={nastavi}
                />
              ))}
            </SimpleGrid>
          </>
        )}
        {napaka && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="space-between" gap="xs">
          {spremenjeno ? (
            <Button
              variant="subtle"
              onClick={() => void poslji('ponastavi')}
              loading={dela === 'ponastavi'}
              disabled={dela !== null}
            >
              Ponastavi na izbiro asistenta
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="subtle" onClick={zapri}>
              Prekliči
            </Button>
            <Button onClick={() => void poslji('shrani')} loading={dela === 'shrani'} disabled={dela !== null}>
              Prikaži
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

function PoljeParametra({
  p,
  vrednost,
  napaka,
  nastavi,
}: {
  p: Polje;
  vrednost: Vrednost | undefined;
  napaka?: string;
  nastavi: (ime: string, v: Vrednost) => void;
}) {
  const ime = p.ime ?? '';
  const filter = p.skupina !== 'GLAVNO';
  switch (p.vrsta) {
    case 'DATUM':
      return (
        <DatePickerInput
          label={p.naziv}
          valueFormat="D. M. YYYY"
          value={typeof vrednost === 'string' ? vrednost : null}
          onChange={(v) => nastavi(ime, v)}
          clearable={filter}
          error={napaka}
        />
      );
    case 'IZBIRA':
    case 'DANE':
      return (
        <Select
          label={p.naziv}
          data={
            p.vrsta === 'DANE'
              ? [
                  { value: 'true', label: 'Da' },
                  { value: 'false', label: 'Ne' },
                ]
              : (p.moznosti ?? []).map((m) => ({ value: m.vrednost ?? '', label: m.naziv ?? m.vrednost ?? '' }))
          }
          value={vrednost === null || vrednost === undefined ? null : String(vrednost)}
          onChange={(v) => nastavi(ime, p.vrsta === 'DANE' ? (v === null ? null : v === 'true') : v)}
          clearable={filter}
          placeholder={filter ? 'Vse' : undefined}
          allowDeselect={filter}
          error={napaka}
        />
      );
    case 'STEVILO':
      return (
        <NumberInput
          label={p.naziv}
          value={typeof vrednost === 'number' ? vrednost : ''}
          onChange={(v) => nastavi(ime, typeof v === 'number' ? v : null)}
          decimalSeparator=","
          thousandSeparator="."
          error={napaka}
        />
      );
    default:
      return (
        <TextInput
          label={p.naziv}
          value={typeof vrednost === 'string' ? vrednost : ''}
          onChange={(e) => nastavi(ime, e.currentTarget.value)}
          error={napaka}
        />
      );
  }
}
